import {useEffect, useRef, useState, type DragEvent} from 'react'

type UseFileDropOptions = {
  onFiles: (files: File[]) => void
  onFolderRejected?: () => void
}

/** `dragover` and a global `window` listener need `preventDefault` or the browser opens the file; the depth
 * counter stops flicker over children; `webkitGetAsEntry` rejects folders before they become unreadable blobs. */
export function useFileDrop({onFiles, onFolderRejected}: UseFileDropOptions) {
  const [active, setActive] = useState(false)
  const depth = useRef(0)

  useEffect(() => {
    const preventNavigation = (e: globalThis.DragEvent) => e.preventDefault()
    window.addEventListener('dragover', preventNavigation)
    window.addEventListener('drop', preventNavigation)
    return () => {
      window.removeEventListener('dragover', preventNavigation)
      window.removeEventListener('drop', preventNavigation)
    }
  }, [])

  function onDragEnter(e: DragEvent) {
    e.preventDefault()
    depth.current += 1
    setActive(true)
  }

  function onDragLeave(e: DragEvent) {
    e.preventDefault()
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setActive(false)
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault()
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    depth.current = 0
    setActive(false)
    const items = e.dataTransfer.items
    if (items.length > 0) {
      const hasFolder = Array.from(items).some((item) => item.webkitGetAsEntry?.()?.isDirectory)
      if (hasFolder) {
        onFolderRejected?.()
        return
      }
    }
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) onFiles(files)
  }

  return {active, zoneProps: {onDragEnter, onDragLeave, onDragOver, onDrop}}
}
